import {CommerceAPI} from 'commerce-api'
import {computed, flow, makeObservable, observable} from 'mobx'
import {RootStore} from './RootStore'
import {PromiseFlow, StoreModel} from './utils'
import {Status, StatusMap} from '../types/commerce'

export interface PromotionResponse {
  c_contentAsset: string
  groups?: any[]
  c_groupLabels?: string[]
  t?: string
  c_isMealDealLanding: boolean
  c_promotionOverrideMessage: string
  c_rulesetJSON: string
  calloutMsg: string
  id: string
  name: string
  startDate: string
  price?: string
  _type: 'promotion'
  details: string
}

export interface NormalizedPromotion extends Omit<PromotionResponse, '_type' | 'c_rulesetJSON'> {
  productIds: string[]
  discountValue: string[]
  discountName: string
  groups: {
    id: string
  }[]
  groupLabels?: string[]
  price?: string
  t?: string
}

export class PromotionStore implements StoreModel {
  api: CommerceAPI
  promotions: Record<string, NormalizedPromotion>
  status: Status

  constructor(rootStore: RootStore, initialState = {promotions: {}, status: StatusMap.IDLE}) {
    this.promotions = initialState?.promotions
    this.status = initialState?.status

    makeObservable(this, {
      status: observable,
      promotions: observable,
      isRequestProcessing: computed,
      fetchPromotionsByIds: flow.bound,
    })
    this.api = rootStore.api
  }

  get asJson() {
    return {
      promotions: this.promotions,
    }
  }

  get isRequestProcessing() {
    return this.status === StatusMap.PENDING
  }

  *fetchPromotionsByIds(ids: string[]): PromiseFlow<any> {
    try {
      this.status = StatusMap.PENDING

      const nonExistedPromotionIds = ids?.filter((id) => !this.promotions.hasOwnProperty(id)) ?? []

      if (!nonExistedPromotionIds.length) return

      const promotions = yield this.api.shopperPromotions.getPromotionsByIds({
        parameters: {promotionsIds: nonExistedPromotionIds},
      })

      if (!promotions?.data) return

      for (const promotion of promotions.data as PromotionResponse[]) {
        this.promotions[promotion.id] = transformPromotion(promotion)
      }
      this.status = StatusMap.DONE
    } catch (error) {
      this.status = StatusMap.ERROR
      console.error(`Failed to fetch promotion with ids: ${ids}`, error)
    }
  }
}

const transformPromotion = (promotion: PromotionResponse): NormalizedPromotion => {
  const {
    groups,
    discountName,
    discountValue,
  }: {
    groups: {id: string; ids: string[]}[]
    discountName: string
    discountValue: string[]
  } = JSON.parse(promotion.c_rulesetJSON)

  return {
    id: promotion.id,
    name: promotion.name,
    productIds: groups.flatMap(({ids}) => ids),
    groups,
    discountName,
    discountValue,
    c_contentAsset: promotion.c_contentAsset,
    c_isMealDealLanding: promotion.c_isMealDealLanding,
    c_promotionOverrideMessage: promotion.c_promotionOverrideMessage,
    calloutMsg: promotion.calloutMsg,
    startDate: promotion.startDate,
    groupLabels: promotion.c_groupLabels,
    price: promotion.price,
    t: promotion.t,
    details: promotion.details,
  }
}
